import React from "react";
import { Link } from "react-router-dom";
import { Eye, ShieldWarning, Bug, Lightbulb, ChartLineUp, PlugsConnected, ArrowRight, Cube, Lightning, Sparkle, CaretDown, EnvelopeSimple, CalendarCheck, Cpu, Briefcase, Strategy, Heartbeat, ChartBar, Wrench, Scales, HardHat, Clock, TrendUp, Target, Buildings } from "@phosphor-icons/react";
import { DEMO_BOOKING_URL, DEMO_USES_EXTERNAL } from "@/lib/demoBooking";
import { BrandMark } from "@/components/Brand";
import useSeo from "@/lib/useSeo";
import { INDUSTRIES } from "@/lib/industries";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/57217f32-30c9-44c9-916a-caaba2b55917/images/8297e900d322a1043443c3679f73a02a72c1125bca465354d94e628d0c299137.png";

const INDUSTRY_ICON = { Cpu, Briefcase, Strategy, Heartbeat, ChartBar, Wrench, Scales, HardHat, Cube };
// Featured sectors emphasised in the hero, sectors grid, and signup flow.
// Source request: "emphasise IT, consultancy, finance, and healthcare".
const FEATURED_INDUSTRY_SLUGS = new Set([
  "technology_saas", "consulting_pmo", "finance_fpa", "healthcare_ops",
]);
// Render featured first so they always lead the grid.
const SECTOR_TILES = (() => {
  const noGeneral = INDUSTRIES.filter((i) => i.slug !== "general");
  const featured = noGeneral.filter((i) => FEATURED_INDUSTRY_SLUGS.has(i.slug));
  const rest = noGeneral.filter((i) => !FEATURED_INDUSTRY_SLUGS.has(i.slug));
  return [...featured, ...rest];
})();

const LENSES = [
  { key: "assumption", title: "Assumption Lens", desc: "Validate every team assumption with NLP scoring and counter-factual reasoning.", icon: Eye, color: "#0020F5" },
  { key: "risk", title: "Risk Lens", desc: "Forecast risks across an impact × likelihood heatmap with severity predictions.", icon: ShieldWarning, color: "#FF3B30" },
  { key: "issue", title: "Issue Lens", desc: "Flag early issues. AI surfaces root causes and contributing factors in seconds.", icon: Bug, color: "#FF9500" },
  { key: "solution", title: "Solution Lens", desc: "Generate three distinct mitigation paths with effort, impact, and step plans.", icon: Lightbulb, color: "#0020F5" },
  { key: "insights", title: "Insights Lens", desc: "Executive synthesis: a project health score, alerts, and prioritised actions.", icon: ChartLineUp, color: "#09090B" },
  { key: "integration", title: "Integration Lens", desc: "Connects to CSV, Excel and PDF exports from any project management or issue tracking tool — drop the file, we handle the rest.", icon: PlugsConnected, color: "#00C7BE" },
];

const Mark = ({ invert = false }) => (
  <BrandMark size={36} testid="brand-mark" tagline="AI-Driven Project Intelligence" invert={invert} />
);

export default function Landing() {
  useSeo({
    title: "ARISSAI® · AI-Powered Project Intelligence · Six lenses, one truth",
    description: "ARISSAI® is the AI-powered project intelligence platform built for PMOs, PMP-certified PMs and transformation leads. Six AI lenses (Assumption · Risk · Issue · Solution · Insights · Integration) — powered by Claude Sonnet 4.5 + GPT-5.2. Try free.",
    path: "/",
  });
  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Top Nav */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-zinc-200 backdrop-saturate-150">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Mark />
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-zinc-700">
            <a href="#about" className="hover:text-zinc-950" data-testid="nav-about">About</a>
            {/* Features dropdown — hover to expand */}
            <div className="relative group" data-testid="nav-features">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 hover:text-zinc-950"
                aria-haspopup="true"
                data-testid="nav-features-trigger"
              >
                Features <CaretDown size={12} weight="bold" />
              </button>
              <div
                className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200"
                data-testid="nav-features-menu"
              >
                <div className="w-60 bg-white border border-zinc-200 shadow-xl py-2">
                  <a href="#problem" className="block px-5 py-3 text-sm font-medium hover:bg-zinc-100" data-testid="nav-features-problem">
                    <div className="font-semibold">The problem</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">Why projects actually fail</div>
                  </a>
                  <a href="#lenses" className="block px-5 py-3 text-sm font-medium hover:bg-zinc-100" data-testid="nav-features-lenses">
                    <div className="font-semibold">Lenses</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">Six AI-powered workflows</div>
                  </a>
                  <a href="#sectors" className="block px-5 py-3 text-sm font-medium hover:bg-zinc-100" data-testid="nav-features-sectors">
                    <div className="font-semibold">Target sectors</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">9 industries we already serve</div>
                  </a>
                  <a href="#works-with" className="block px-5 py-3 text-sm font-medium hover:bg-zinc-100" data-testid="nav-features-integrations">
                    <div className="font-semibold">Integrations</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">CSV / Excel / PDF imports</div>
                  </a>
                  <Link to="/case-studies" className="block px-5 py-3 text-sm font-medium hover:bg-zinc-100" data-testid="nav-features-case-studies">
                    <div className="font-semibold">Case studies</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">Real customer wins</div>
                  </Link>
                  <Link to="/pricing" className="block px-5 py-3 text-sm font-medium hover:bg-zinc-100" data-testid="nav-features-pricing">
                    <div className="font-semibold">Pricing</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">Plans & subscription tiers</div>
                  </Link>
                </div>
              </div>
            </div>
            <a href="mailto:hello@arissai.com" className="inline-flex items-center gap-1.5 hover:text-zinc-950" data-testid="nav-contact">
              <EnvelopeSimple size={14} weight="duotone" /> Contact
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={DEMO_BOOKING_URL}
              target={DEMO_USES_EXTERNAL ? "_blank" : undefined}
              rel={DEMO_USES_EXTERNAL ? "noopener noreferrer" : undefined}
              className="hidden md:inline-flex items-center gap-1.5 px-4 h-10 text-sm font-medium border border-zinc-200 hover:border-zinc-950 hover:bg-zinc-950 hover:text-white transition-colors"
              data-testid="nav-book-demo"
            >
              <CalendarCheck size={14} weight="bold" /> Book a demo
            </a>
            <Link to="/login" className="hidden sm:inline-flex items-center px-4 h-10 text-sm font-medium hover:bg-zinc-100" data-testid="nav-login">Sign in</Link>
            <Link to="/signup" className="inline-flex items-center px-4 h-10 text-sm font-semibold bg-zinc-950 text-white hover:bg-zinc-800 transition-colors" data-testid="nav-signup">
              Get started <ArrowRight size={16} weight="bold" className="ml-2" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-20 sm:pt-28 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 h-7 bg-white border border-zinc-200 text-[11px] tracking-[0.2em] uppercase font-bold text-zinc-700" data-testid="hero-eyebrow">
              <Sparkle size={12} weight="fill" /> AI-Powered Project Intelligence
            </div>
            <h1 className="mt-6 font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              Six lenses. <br />
              <span className="text-zinc-400">One</span> <span className="text-[#0020F5]">truth</span> about your project.
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-zinc-600 leading-relaxed">
              ARISSAI runs every assumption, risk, and issue through a panel of AI lenses — so PMs decide with confidence, not intuition.
            </p>
            <p className="mt-3 text-sm text-zinc-500">
              Purpose-built for <span className="font-semibold text-zinc-800">IT &amp; SaaS</span>, <span className="font-semibold text-zinc-800">Consulting &amp; PMO</span>, <span className="font-semibold text-zinc-800">Enterprise Finance &amp; FP&amp;A</span>, and <span className="font-semibold text-zinc-800">Healthcare Operations</span>.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" className="inline-flex items-center px-6 h-12 text-sm font-semibold bg-zinc-950 text-white hover:bg-zinc-800 transition-colors" data-testid="hero-cta-primary">
                Start free tour <ArrowRight size={16} weight="bold" className="ml-2" />
              </Link>
              <a href="#lenses" className="inline-flex items-center px-6 h-12 text-sm font-semibold border border-zinc-950 hover:bg-zinc-100 transition-colors" data-testid="hero-cta-secondary">
                Explore the lenses
              </a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-zinc-500 uppercase tracking-[0.2em] font-bold">
              <span>ARISSAI AI</span>
              <span className="w-1 h-1 bg-zinc-400" />
              <span>Built in Canada</span>
              <span className="w-1 h-1 bg-zinc-400" />
              <span>CSV · Excel · PDF</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-square border border-zinc-200 bg-zinc-950 overflow-hidden">
              {/* Sora 2 generated hero loop — falls back to HERO_IMG if MP4 not yet uploaded */}
              <video
                src="/hero-loop.mp4"
                poster={HERO_IMG}
                autoPlay
                loop
                muted
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="hero-loop-video"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <img
                src={HERO_IMG}
                alt="Six glass lenses"
                className="absolute inset-0 w-full h-full object-cover -z-10"
              />
              <div className="absolute inset-0 bg-black/30 mix-blend-multiply pointer-events-none" />
              <div className="absolute left-4 top-4 right-4 flex items-center justify-between text-[10px] tracking-[0.2em] font-bold uppercase text-white/85">
                <span>ARISSAI · LENS ARRAY</span>
                <span>v1.0</span>
              </div>
              <div className="absolute left-4 bottom-4 right-4 flex items-center justify-between text-[10px] tracking-[0.2em] font-bold uppercase text-white/85">
                <span>Live · 6/6 online</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 bg-[#00C7BE] animate-pulse" /> active</span>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-zinc-950 text-white p-5 w-56 hidden sm:block border border-zinc-800">
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 font-bold">Project health</div>
              <div className="font-display font-black text-4xl mt-2">87<span className="text-zinc-500 text-xl">/100</span></div>
              <div className="text-xs text-zinc-400 mt-1">2 critical · 4 high risks</div>
            </div>
          </div>
        </div>

        {/* Ticker */}
        <div className="border-y border-zinc-200 bg-white overflow-hidden">
          <div className="ticker-track flex whitespace-nowrap py-4 gap-12 text-xs font-bold tracking-[0.2em] uppercase text-zinc-500">
            {[...Array(2)].map((_, k) => (
              <div className="flex gap-12" key={`ticker-loop-${k}`}>
                <span>Assumption Validation</span><span>·</span>
                <span>Risk Heatmaps</span><span>·</span>
                <span>Root Cause Detection</span><span>·</span>
                <span>AI Mitigation Plans</span><span>·</span>
                <span>Executive Insights</span><span>·</span>
                <span>CSV · Excel · PDF Ingestion</span><span>·</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----------- The Problem (what ARISSAI is solving) ----------- */}
      <section id="problem" className="bg-zinc-950 text-white py-24 sm:py-32 border-t border-zinc-800" data-testid="section-problem">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#9DA8FF]">// The problem</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-4 leading-[1.05]">
                Projects don&apos;t fail<br />from bad decisions —<br />
                <span className="text-[#FF9500]">they fail from</span><br />
                <span className="text-[#FF3B30]">unchecked assumptions.</span>
              </h2>
              <p className="text-zinc-300 mt-6 text-base sm:text-lg leading-relaxed">
                PMs and PMOs juggle CSVs, PDFs and meeting notes that bury the assumptions, risks and issues that actually move the needle. Spreadsheets don&apos;t grade severity. Status reports lie. Executive briefs lag a week. By the time the steering committee asks &ldquo;are we sure?&rdquo;, the truth is already buried in 14 tabs.
              </p>
              <p className="text-white mt-5 text-base sm:text-lg leading-relaxed">
                <strong>ARISSAI replaces gut-feel with graded intelligence.</strong> Drop any project document — CSV, PDF, Excel, SOW, minutes, financials — and six AI lenses extract every assumption, risk and issue, grade them, propose 3 mitigation paths per item, and ship a PowerBI-style executive brief in under a minute.
              </p>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4" data-testid="problem-pain-grid">
              {[
                { tag: "Pain", t: "10 hours/week", b: "PMs spend writing status reports nobody reads twice.", c: "#FF3B30" },
                { tag: "Pain", t: "60% of risks", b: "in a typical project register are missing, mis-graded, or stale.", c: "#FF9500" },
                { tag: "Pain", t: "1 week lag", b: "between when a risk is known and when the steering committee hears about it.", c: "#FFD60A" },
                { tag: "Pain", t: "5+ tools", b: "to stitch together: PM software, Excel, slides, status doc, email.", c: "#7C3AED" },
                { tag: "Cure", t: "60 seconds", b: "from CSV upload to AI-graded register + executive headline.", c: "#16A34A" },
                { tag: "Cure", t: "0 to 100", b: "deterministic Project Health Score. Same number on every dashboard.", c: "#0020F5" },
              ].map((p) => (
                <div key={`${p.tag}-${p.t}`} className="bg-zinc-900 border border-zinc-800 p-5 hover:bg-zinc-800 transition-colors">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold" style={{ color: p.c }}>{p.tag}</div>
                  <div className="font-display font-black text-3xl mt-2" style={{ color: p.c }}>{p.t}</div>
                  <div className="text-sm text-zinc-300 mt-2 leading-relaxed">{p.b}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Audience-specific value props for the 4 emphasised sectors */}
          <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="problem-audience-grid">
            {[
              { slug: "technology_saas",  who: "IT & SaaS",                line1: "Roadmaps shifting weekly,", line2: "release risk is invisible.", acc: "#0020F5" },
              { slug: "consulting_pmo",   who: "Consulting & PMO",         line1: "Every client engagement",    line2: "is a different spreadsheet.", acc: "#7C3AED" },
              { slug: "finance_fpa",      who: "Finance & FP&A",           line1: "Capital approval decks",     line2: "never match delivery reality.", acc: "#16A34A" },
              { slug: "healthcare_ops",   who: "Healthcare Operations",    line1: "Auditable risk tracking",    line2: "across care-delivery programs.", acc: "#00C7BE" },
            ].map((a) => (
              <Link
                key={a.slug}
                to={`/signup?industry=${a.slug}`}
                className="group bg-zinc-900 border border-zinc-800 p-5 hover:border-white transition-colors"
                data-testid={`problem-audience-${a.slug}`}
              >
                <div className="w-1.5 h-8" style={{ background: a.acc }} />
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-400 mt-4">For</div>
                <div className="font-display font-bold text-lg text-white mt-1">{a.who}</div>
                <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                  {a.line1}<br />{a.line2}
                </p>
                <div className="mt-5 text-[10px] tracking-[0.2em] uppercase font-bold text-white inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                  Start for this sector <ArrowRight size={11} weight="bold" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About ARISSAI — Why Lenses (narrative only) */}
      <section id="about" className="bg-zinc-100 border-t border-zinc-200 py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">// About ARISSAI</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-4 leading-tight">
                Intelligent decisions, <br />for every project.
              </h2>
              <p className="text-zinc-700 mt-6 text-base sm:text-lg leading-relaxed max-w-2xl">
                ARISSAI is a cloud-based SaaS platform that empowers project managers with intelligent decision-making. It uses six AI-powered lenses to analyze assumptions, risks, issues, and generate solutions.
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white border border-zinc-200 p-7" data-testid="why-lenses-card">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#0020F5]">// Why "Lenses"?</div>
                <p className="text-base text-zinc-800 mt-4 leading-relaxed">
                  <strong>ARISSAI</strong> stands for <strong>A</strong>ssumptions, <strong>R</strong>isks, <strong>I</strong>ssues, and <strong>S</strong>olutions, powered by <strong>AI</strong>. Each lens delivers insight.
                </p>
                <div className="mt-5 flex flex-wrap gap-1.5">
                  {["A — Assumptions", "R — Risks", "I — Issues", "S — Solutions", "AI"].map((tag) => (
                    <span key={tag} className="inline-flex items-center h-7 px-2.5 bg-zinc-950 text-white text-[10px] tracking-[0.15em] uppercase font-bold">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lenses */}
      <section id="lenses" className="relative bg-white py-24 sm:py-32">
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-5">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">// The Six Lenses</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-4 leading-tight">
                Every angle covered. <br /> Every decision defended.
              </h2>
            </div>
            <p className="lg:col-span-6 lg:col-start-7 text-zinc-600 text-base sm:text-lg leading-relaxed self-end">
              Each lens is an independent AI workflow. Together, they form a closed loop: assumptions feed risks, risks surface issues, issues spawn solutions, and insights summarise the whole.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {LENSES.map((l, i) => {
              const Icon = l.icon;
              return (
                <div key={l.key} data-testid={`lens-card-${l.key}`} className="group bg-white p-8 transition-transform duration-300 hover:-translate-y-1 hover:bg-zinc-50">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 grid place-items-center" style={{ background: l.color, color: "#fff" }}>
                      <Icon size={22} weight="duotone" />
                    </div>
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-400">0{i + 1}</div>
                  </div>
                  <h3 className="font-display font-bold text-2xl mt-6">{l.title}</h3>
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{l.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-zinc-950 text-white py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">// Workflow</div>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-4 max-w-3xl">
            Drop in your project. Run the lenses. Ship decisions.
          </h2>

          <div className="grid lg:grid-cols-3 gap-px bg-zinc-800 mt-16 border border-zinc-800">
            {[
              { n: "01", t: "Import", d: "Upload a CSV, Excel or PDF — including exports from any project management or issue tracking tool. ARISSAI ingests assumptions, risks, and issues automatically.", icon: PlugsConnected },
              { n: "02", t: "Analyse", d: "Each item is scored by the relevant lens. Risk heatmaps populate. Issues get root-cause analysis.", icon: Lightning },
              { n: "03", t: "Decide", d: "Solution Lens proposes three paths. Insights Lens delivers the exec brief. You decide, fast.", icon: Cube },
            ].map((s) => {
              const I = s.icon;
              return (
                <div key={s.n} className="bg-zinc-950 p-10">
                  <div className="text-zinc-500 font-display font-black text-xl">{s.n}</div>
                  <I size={28} weight="duotone" className="mt-6 text-white" />
                  <h3 className="font-display font-bold text-2xl mt-6">{s.t}</h3>
                  <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Outcomes — what ARISSAI helps teams do */}
      <section id="why" className="py-24 sm:py-32 bg-zinc-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
            <div className="lg:col-span-6">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">// What ARISSAI helps you do</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-4 leading-tight">
                From scattered data <br />to <span className="text-[#0020F5]">decisions you can defend</span>.
              </h2>
            </div>
            <p className="lg:col-span-6 text-zinc-700 text-base sm:text-lg leading-relaxed">
              Four practical outcomes — built on the six AI lenses, delivered every time you log a risk, upload a file, or open the dashboard.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-300 border border-zinc-300" data-testid="outcomes-grid">
            {[
              {
                icon: Clock,
                color: "#0020F5",
                title: "Reduce project delays",
                desc: "AI-driven forecasting flags slippage before it shows up in your Gantt chart.",
              },
              {
                icon: TrendUp,
                color: "#FF3B30",
                title: "Track risks, budgets & timelines in real time",
                desc: "One health score, six lenses, instant traffic-light view across every project.",
              },
              {
                icon: Lightning,
                color: "#FF9500",
                title: "Help teams make faster decisions",
                desc: "Each lens delivers three concrete options — no analysis paralysis, no decks.",
              },
              {
                icon: Sparkle,
                color: "#7C3AED",
                title: "AI insights for complex commercial decisions",
                desc: "Steering-committee-ready reasoning, with confidence scores and counter-risks.",
              },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.title} className="bg-white p-7" data-testid={`outcome-${card.title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/-+$/, '')}`}>
                  <div className="w-11 h-11 grid place-items-center text-white" style={{ background: card.color }}>
                    <Icon size={20} weight="duotone" />
                  </div>
                  <h3 className="font-display font-bold text-xl mt-5 leading-tight">{card.title}</h3>
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{card.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Case study stat block */}
          <div className="mt-10 bg-zinc-950 text-white grid lg:grid-cols-12 gap-8 items-center p-8 sm:p-10" data-testid="case-study-stat">
            <div className="lg:col-span-4 border-l-4 border-[#0020F5] pl-6">
              <div className="font-display font-black text-6xl sm:text-7xl leading-none">60<span className="text-[#0020F5]">%</span></div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mt-3">Reduction in PMO reporting time</div>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">// Case study</div>
              <p className="text-lg sm:text-xl mt-3 leading-relaxed text-zinc-100">
                "ARISSAI cut our weekly reporting cycle from <strong className="text-white">two days</strong> to under <strong className="text-white">a single afternoon</strong>. The PMO brief is the first artefact our steering committee reads."
              </p>
              <div className="text-xs tracking-[0.18em] uppercase font-bold text-zinc-500 mt-5">Pilot engagement · Enterprise PMO · 2026</div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Sectors */}
      <section id="sectors" className="bg-white border-t border-zinc-200 py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-7">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">// Target sectors</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-4 leading-tight">
                Sectors we <span className="text-[#0020F5]">already serve</span>.
              </h2>
              <p className="text-zinc-600 mt-5 text-base sm:text-lg leading-relaxed max-w-2xl">
                Pick your sector on signup and ARISSAI tunes its lens vocabulary, KPI labels, and dashboard tone to match your domain.
              </p>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
                <Target size={12} weight="bold" /> Top 4 — IT · Consulting · Finance · Healthcare
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200" data-testid="sectors-grid">
            {SECTOR_TILES.map((sector) => {
              const Icon = INDUSTRY_ICON[sector.icon] || Buildings;
              const featured = FEATURED_INDUSTRY_SLUGS.has(sector.slug);
              return (
                <Link
                  key={sector.slug}
                  to={`/signup?industry=${sector.slug}`}
                  className={
                    "group bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:bg-zinc-50 " +
                    (featured ? "ring-2 ring-inset ring-zinc-950 relative" : "")
                  }
                  data-testid={`sector-tile-${sector.slug}`}
                >
                  {featured && (
                    <span className="absolute top-0 right-0 bg-zinc-950 text-white text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-1">
                      Top
                    </span>
                  )}
                  <div className="w-11 h-11 grid place-items-center text-white transition-transform duration-300 group-hover:scale-110" style={{ background: sector.accent }}>
                    <Icon size={20} weight="duotone" />
                  </div>
                  <div className={"font-display font-bold mt-5 leading-tight " + (featured ? "text-xl" : "text-base")}>{sector.label}</div>
                  <div className="text-xs text-zinc-500 mt-2 leading-relaxed">{sector.short}</div>
                  <div className="mt-5 text-[11px] tracking-[0.2em] uppercase font-bold inline-flex items-center gap-1.5 text-zinc-950 group-hover:gap-2.5 transition-all">
                    Start <ArrowRight size={11} weight="bold" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* The Challenge — Innovation Challenge problem statement */}
      <section id="challenge" className="bg-zinc-950 text-white py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#0020F5]">// The challenge</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-4 leading-tight">
                Why ARISSAI <br />exists.
              </h2>
              <p className="text-zinc-300 mt-6 text-base sm:text-lg leading-relaxed">
                Project managers face increasing complexity, uncertainty, and pressure to deliver results across industries. Yet many rely on fragmented tools and manual processes that fail to surface hidden risks, validate assumptions, or generate timely solutions.
              </p>
              <p className="text-zinc-300 mt-4 text-base sm:text-lg leading-relaxed">
                This Innovation Challenge addresses that gap by developing <strong className="text-white">ARISSAI — an AI-powered platform that transforms how project leaders make decisions.</strong>
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-5">// Resolving this challenge will</div>
              <ul className="grid gap-px bg-zinc-800 border border-zinc-800" data-testid="challenge-outcomes">
                {[
                  "Enable smarter, faster project decisions through AI-driven insights",
                  "Reduce project failure rates by identifying risks and issues early",
                  "Improve team alignment by validating assumptions and surfacing root causes",
                  "Enhance reporting and accountability with centralised intelligence",
                  "Support scalable integration with existing enterprise tools",
                  "Lay the foundation for a new category of intelligent project management solutions",
                ].map((line, i) => (
                  <li key={line} className="bg-zinc-950 p-5 flex gap-5" data-testid={`challenge-outcome-${i + 1}`}>
                    <span className="font-mono text-[11px] tracking-widest text-[#0020F5] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm text-zinc-200 leading-relaxed">{line}</span>
                  </li>
                ))}
              </ul>
              <p className="text-zinc-400 italic mt-6 text-sm leading-relaxed">
                By solving this challenge, we unlock a future where project leaders are empowered with real-time, predictive, and actionable intelligence — not just data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Works with — accepted data sources (no third-party brand names) */}
      <section id="works-with" className="bg-white border-t border-zinc-200 py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-6">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">// Works with</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-4 leading-tight">
                Plug into the tools <br />your team already uses.
              </h2>
            </div>
            <p className="lg:col-span-6 text-zinc-600 text-base leading-relaxed">
              ARISSAI accepts <strong>CSV</strong>, <strong>Excel</strong>, and <strong>PDF</strong> exports from any <strong>project management tool</strong> or <strong>issue tracking tool</strong>. Drop the file, the lenses do the rest. Native two-way API connectors are on our roadmap.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200" data-testid="works-with-grid">
            {[
              { name: "CSV", subtitle: "Universal export", testid: "csv" },
              { name: "Excel", subtitle: "Spreadsheet workbook", testid: "excel" },
              { name: "PDF", subtitle: "Reports & briefs", testid: "pdf" },
              { name: "PM Tools", subtitle: "Any project management tool export", testid: "pm-tools" },
            ].map((t) => (
              <div
                key={t.testid}
                className="bg-white p-6 text-center transition-colors hover:bg-zinc-50"
                data-testid={`works-with-${t.testid}`}
              >
                <div className="font-display font-bold text-lg leading-tight">{t.name}</div>
                <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-500 mt-2">{t.subtitle}</div>
              </div>
            ))}
          </div>

          {/* Roadmap — Coming soon native API connectors */}
          <div className="mt-10 bg-zinc-950 text-white p-8 sm:p-10 grid lg:grid-cols-12 gap-8 items-center" data-testid="connector-roadmap">
            <div className="lg:col-span-2">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">// Coming soon</div>
              <div className="font-display font-black text-3xl mt-2 leading-none">Q3<span className="text-zinc-500">.26</span></div>
            </div>
            <div className="lg:col-span-7">
              <h3 className="font-display font-bold text-2xl">Native two-way connectors are on the roadmap.</h3>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                We're working through partner-program reviews to bring real-time sync to popular project management and issue tracking platforms. Until then, file exports keep you fully covered — and your data stays in your control.
              </p>
            </div>
            <div className="lg:col-span-3 lg:text-right">
              <Link to="/signup" className="inline-flex items-center gap-2 h-11 px-5 bg-white text-zinc-950 text-sm font-bold hover:bg-zinc-200" data-testid="roadmap-cta">
                Start with exports <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0020F5] text-white py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <h2 className="lg:col-span-8 font-display font-black tracking-tighter text-4xl sm:text-5xl">
            Run your next steering committee through the lenses.
          </h2>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/signup" className="inline-flex items-center px-8 h-14 text-sm font-bold bg-white text-[#0020F5] hover:bg-zinc-100 transition-colors" data-testid="cta-signup">
              Create your workspace <ArrowRight size={16} weight="bold" className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-2">
            <Mark />
            <p className="text-sm text-zinc-600 mt-4 max-w-sm leading-relaxed">
              AI-powered project intelligence for project managers who need to decide with confidence — not intuition.
            </p>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Product</div>
            <ul className="mt-4 space-y-2 text-sm text-zinc-700">
              <li><a href="#about" className="hover:text-zinc-950" data-testid="footer-about">About</a></li>
              <li><a href="#lenses" className="hover:text-zinc-950" data-testid="footer-lenses">Lenses</a></li>
              <li><a href="#sectors" className="hover:text-zinc-950" data-testid="footer-sectors">Target sectors</a></li>
              <li><a href="#works-with" className="hover:text-zinc-950" data-testid="footer-integrations">Integrations</a></li>
              <li><Link to="/pricing" className="hover:text-zinc-950" data-testid="footer-pricing">Pricing</Link></li>
              <li><Link to="/case-studies" className="hover:text-zinc-950" data-testid="footer-case-studies">Case studies</Link></li>
              <li><Link to="/press" className="hover:text-zinc-950" data-testid="footer-press">Press &amp; investors</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Contact</div>
            <ul className="mt-4 space-y-2 text-sm text-zinc-700">
              <li>
                <a
                  href="mailto:hello@arissai.com"
                  className="inline-flex items-center gap-2 font-mono hover:text-[#0020F5]"
                  data-testid="footer-contact-email"
                >
                  <EnvelopeSimple size={14} weight="duotone" /> hello@arissai.com
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-zinc-200 max-w-7xl mx-auto px-6 sm:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500 font-mono">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>© 2026 ARISSAI®</span>
            <Link to="/privacy" className="hover:text-zinc-950 underline-offset-2 hover:underline" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/terms" className="hover:text-zinc-950 underline-offset-2 hover:underline" data-testid="footer-terms-link">Terms</Link>
            <Link to="/legal" className="hover:text-zinc-950 underline-offset-2 hover:underline" data-testid="footer-legal-link">IP</Link>
          </div>
          <div>Built in Canada · Powered by ARISSAI AI</div>
        </div>
      </footer>

      {/* Industry detail panel removed — section deprecated 2026-02-15 */}
    </div>
  );
}
